import type { LocaleCode, RecordableOperation } from '@/api/models';

/**
 * The command picker's pure half: matching, ranking, grouping.
 *
 * Everything here is a function of its arguments, which is what lets the fiddly
 * parts — what a two-character query matches, which row Enter chooses, whether a
 * never-used operation is still reachable — be exercised without rendering
 * anything or pressing a key.
 */

/** How many frequent shortcuts the resting state offers. */
export const SHORTCUT_LIMIT = 4;

/**
 * The DOM id of the search input, and of each row.
 *
 * Real ids rather than test props, because behaviour depends on them:
 * `aria-activedescendant` points at a row by id, and the highlight is scrolled
 * into view by looking a row up by id rather than threading a ref through two
 * HeroUI compound components. They live here so the View that writes them and
 * the hook that reads them cannot disagree about the format.
 */
export const SEARCH_INPUT_ID = 'operations-record-picker-search-input';

export const rowElementId = (key: string): string =>
    `operations-record-picker-option-${key}`;

/**
 * Below this many operations the shortcuts *are* the list, and showing both
 * would be the same four rows twice.
 */
export const SHORTCUT_MIN_OPERATIONS = SHORTCUT_LIMIT + 1;

/**
 * Case- and diacritic-insensitive comparison key.
 *
 * `NFD` then strip combining marks, so a query typed without accents still finds
 * a name written with them. Georgian has no combining diacritics, so this is
 * doing its work on the English half — but it costs nothing on the other and a
 * search that only worked in one language would be the bug this picker exists to
 * avoid.
 */
export const fold = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase();

/** Whether a row can be chosen at all. */
export const isSelectable = (operation: RecordableOperation): boolean =>
    operation.unrecordableReason === 'NONE';

/** The operation's name in the reader's language, falling back to the other. */
export const operationName = (
    operation: RecordableOperation,
    locale: LocaleCode,
): string =>
    (locale === 'ka' ? operation.nameKa : operation.nameEn) ||
    operation.nameEn ||
    operation.nameKa;

/** The other language's name — rendered muted under the first. */
export const operationAltName = (
    operation: RecordableOperation,
    locale: LocaleCode,
): string => (locale === 'ka' ? operation.nameEn : operation.nameKa);

/**
 * How well a query matches an operation: lower is better, `null` is no match.
 *
 * Three tiers rather than a fuzzy score. A fuzzy match would let `sae` find
 * "Salary payout", which is clever and wrong here — the thing this list is about
 * to do is post to the ledger, and a picker that guesses is a picker you have to
 * double-check. Both names are searched at every tier, so typing in either
 * language works.
 */
export const matchRank = (
    operation: RecordableOperation,
    query: string,
): number | null => {
    // Trimmed, so a trailing space typed on the way to the next word does not
    // silently empty the list.
    const needle = fold(query.trim());
    if (!needle) {
        return 0;
    }
    const names = [fold(operation.nameKa), fold(operation.nameEn)];

    if (names.some((name) => name.startsWith(needle))) {
        return 0;
    }
    if (
        names.some((name) =>
            name.split(/\s+/).some((word) => word.startsWith(needle)),
        )
    ) {
        return 1;
    }
    return names.some((name) => name.includes(needle)) ? 2 : null;
};

/** How the full list is ordered. */
export type PickerSort = 'used' | 'name';

/**
 * The picker's rows, already grouped the way they render.
 *
 * The split is the design's: one best match ready for Enter, the rest of the
 * selectable matches, then — always last — matches the date has taken away.
 * `selectable` is the flat sequence the arrows walk, so the highlight and the
 * rendering cannot disagree about what "the next row" is.
 */
export type PickerGroups = {
    /** The row Enter chooses when nothing has been arrowed to. Null when nothing matches. */
    best: RecordableOperation | null;
    /** Selectable matches after `best`. */
    others: RecordableOperation[];
    /** Matches that cannot be chosen, with a reason. Never above a selectable row. */
    unavailable: RecordableOperation[];
    /** `best` followed by `others` — what the arrow keys and Enter act on. */
    selectable: RecordableOperation[];
    /** Rows on screen, for the "{shown} of {total}" counter. */
    shown: number;
    /** Every row the caller could see at some point — the counter's denominator. */
    total: number;
};

/**
 * Filter, sort and group.
 *
 * **The server's order is the default**, not a re-sort: it ranks by a 90-day
 * count that the Templates catalog shares, and a second opinion here would make
 * the two screens disagree. `name` re-sorts deliberately, and only that one does.
 *
 * **Match rank never outranks availability.** An unavailable row that starts
 * with the query still sits below every selectable one, because the list is
 * ordered by what the user can do next, not by how well they typed.
 */
export const groupOperations = (
    operations: readonly RecordableOperation[],
    query: string,
    sort: PickerSort,
    locale: LocaleCode,
): PickerGroups => {
    const ranked = operations
        .map((operation, index) => ({
            operation,
            index,
            rank: matchRank(operation, query),
        }))
        .filter((row) => row.rank !== null);

    ranked.sort((a, b) => {
        if (a.rank !== b.rank) {
            return (a.rank as number) - (b.rank as number);
        }
        if (sort === 'name') {
            return operationName(a.operation, locale).localeCompare(
                operationName(b.operation, locale),
            );
        }
        return a.index - b.index;
    });

    const matched = ranked.map((row) => row.operation);
    const selectable = matched.filter(isSelectable);
    const unavailable = matched.filter((item) => !isSelectable(item));

    return {
        // A best match is only meaningful once something has been typed. In the
        // resting state every row is equally "the best", and promoting the first
        // one out of the list would just move it.
        best: query.trim() ? (selectable[0] ?? null) : null,
        others: query.trim() ? selectable.slice(1) : selectable,
        unavailable,
        selectable,
        shown: matched.length,
        total: operations.length,
    };
};

/**
 * The frequent shortcuts, or an empty list.
 *
 * **Only operations actually used.** The section says "you use these, last 90
 * days"; a card with a count of zero would be contradicting its own heading, and
 * a fresh tenant would get four cards for things nobody has ever recorded.
 *
 * **Hidden below five operations**, because at that size the shortcuts are the
 * list and the screen would show the same rows twice.
 */
export const frequentShortcuts = (
    operations: readonly RecordableOperation[],
): RecordableOperation[] => {
    if (operations.length < SHORTCUT_MIN_OPERATIONS) {
        return [];
    }
    return operations
        .filter((item) => isSelectable(item) && item.recordCount > 0)
        .slice(0, SHORTCUT_LIMIT);
};

/**
 * Move a highlight by one row, clamped at both ends.
 *
 * Clamped rather than wrapped: holding the down arrow to reach the bottom of a
 * sixty-row list should stop there, not silently reappear at the top with the
 * user's eyes still at the bottom.
 */
export const moveHighlight = (
    current: number,
    delta: number,
    count: number,
): number => {
    if (count === 0) {
        return 0;
    }
    return Math.min(Math.max(current + delta, 0), count - 1);
};

/**
 * The i18n key and arguments for an unavailable row's reason.
 *
 * Resolved here so the branch is a value a test can assert on, and so the View
 * only ever calls `t`. A reason with no date still gets a sentence — "not
 * available" alone would send the user looking in the wrong place.
 */
export type ReasonCopy = {
    key: string;
    args: { date: string };
};

export const reasonCopy = (
    operation: RecordableOperation,
    formatDate: (iso: string) => string,
): ReasonCopy => {
    const date = operation.reasonDate ? formatDate(operation.reasonDate) : '';
    switch (operation.unrecordableReason) {
        case 'NEEDS_SECOND_AMOUNT':
            return {
                key: 'operations.record.picker.needsSecondAmount',
                args: { date },
            };
        case 'NOT_PUBLISHED':
            return {
                key: 'operations.record.picker.notPublished',
                args: { date },
            };
        case 'WINDOW_ENDED':
            return {
                key: date
                    ? 'operations.record.picker.windowEnded'
                    : 'operations.record.picker.windowEndedUndated',
                args: { date },
            };
        case 'WINDOW_NOT_STARTED':
            return {
                key: date
                    ? 'operations.record.picker.windowNotStarted'
                    : 'operations.record.picker.windowNotStartedUndated',
                args: { date },
            };
        default:
            return {
                key: 'operations.record.picker.unknownReason',
                args: { date },
            };
    }
};
